import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const API = process.env.REACT_APP_API || '/api';
const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];

export default function PublicShare() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [pwd, setPwd] = useState('');
  const [needsPwd, setNeedsPwd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const audioRef = useRef(null);
  const activeWordRef = useRef(null);

  const fetchIt = async (password) => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/share/${token}` + (password ? `?password=${encodeURIComponent(password)}` : ''));
      const body = await res.json();
      if (!res.ok) {
        if (body.password_required) { setNeedsPwd(true); setLoading(false); return; }
        setError(body.error || 'خطأ');
        setLoading(false); return;
      }
      setData(body); setNeedsPwd(false);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  useEffect(() => { fetchIt(); /* eslint-disable-next-line */ }, [token]);
  useEffect(() => { if (audioRef.current) audioRef.current.playbackRate = playbackRate; }, [playbackRate, data]);

  const isVideo = useMemo(() => /\.(mp4|webm|mov|m4v|avi|mkv)(\?|$)/i.test(data?.audio_url || ''), [data]);

  const alignedTokens = useMemo(() => {
    const text = data?.formatted_text || '';
    if (!text) return [];
    const parts = text.split(/(\s+)/);
    const wordCount = parts.filter(p => p.trim()).length;
    const raw = data?.words || [];
    let wordPos = 0;
    return parts.map((part) => {
      if (!part.trim()) return { text: part, isWhitespace: true };
      const rawIdx = raw.length ? Math.min(raw.length - 1, Math.floor((wordPos / Math.max(1, wordCount)) * raw.length)) : -1;
      const rw = rawIdx >= 0 ? raw[rawIdx] : null;
      wordPos++;
      return { text: part, isWhitespace: false, start: rw?.start ?? null, end: rw?.end ?? null };
    });
  }, [data]);

  useEffect(() => {
    if (activeWordRef.current) activeWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [currentTime]);

  const seekTo = (s) => { if (audioRef.current) { audioRef.current.currentTime = s; audioRef.current.play(); } };
  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>جاري التحميل...</div>;

  if (needsPwd) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'var(--bg)' }}>
        <div className="card" style={{ width: '100%', maxWidth: 400, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔒</div>
          <h2>هذا التفريغ محمي بكلمة سر</h2>
          <input className="form-input" type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="كلمة السر" style={{ marginTop: 16 }} onKeyDown={e => e.key === 'Enter' && fetchIt(pwd)}/>
          <button className="btn btn-primary" style={{ width: '100%', marginTop: 12 }} onClick={() => fetchIt(pwd)}>دخول</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
        <div className="card" style={{ width: '100%', maxWidth: 480, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 40 }}>⚠️</div><h2>{error}</h2>
        </div>
      </div>
    );
  }

  if (!data) return null;
  const refs = data.references || [];
  const chapters = Array.isArray(data.chapters) ? data.chapters : [];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: 16 }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
          <img src={`${process.env.PUBLIC_URL || ''}/logo-mark.svg`} alt="" width="36" height="36"/>
          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>منصّة مُفرِّغ</span>
        </header>

        <div className="card" style={{ marginBottom: 16 }}>
          <h1 style={{ marginTop: 0, fontSize: 22 }}>
            {data.is_approved && <span style={{ color: 'var(--primary)', marginInlineEnd: 8 }}>✔</span>}
            {data.title}
          </h1>
          {data.is_approved && (<div style={{ fontSize: 13, color: 'var(--primary)', marginBottom: 8 }}>تفريغ مُراجَع ومعتمد</div>)}
          {data.creator?.name && (<div style={{ fontSize: 12, color: 'var(--text-muted)' }}>أعدّه: {data.creator.name}</div>)}
        </div>

        {data.audio_url && (
          <div className="card" style={{ marginBottom: 16 }}>
            {isVideo ? (
              <video ref={audioRef} src={data.audio_url} controls preload="metadata"
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={e => { e.target.playbackRate = playbackRate; }}
                style={{ width: '100%', maxHeight: 360, background: '#000', borderRadius: 8 }}/>
            ) : (
              <audio ref={audioRef} src={data.audio_url} controls preload="metadata"
                onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
                onLoadedMetadata={e => { e.target.playbackRate = playbackRate; }}
                style={{ width: '100%' }}/>
            )}
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>السرعة:</span>
              {SPEEDS.map(s => (
                <button key={s} onClick={() => setPlaybackRate(s)} className={`btn btn-sm ${playbackRate === s ? 'btn-primary' : 'btn-outline'}`} style={{ padding: '2px 8px', fontSize: 12 }}>
                  {s}x
                </button>
              ))}
            </div>
          </div>
        )}

        {data.summary && (
          <div className="card" style={{ marginBottom: 16, borderInlineStart: '4px solid var(--accent)' }}>
            <div className="card-title" style={{ marginBottom: 8 }}>📝 الملخّص</div>
            <div style={{ lineHeight: 1.9, whiteSpace: 'pre-wrap' }}>{data.summary}</div>
          </div>
        )}

        {chapters.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>📑 الفهرس</div>
            {chapters.map((c, i) => (
              <div key={i} onClick={() => seekTo(c.start)} style={{ display: 'flex', gap: 12, padding: '8px 6px', borderBottom: i < chapters.length - 1 ? '1px solid var(--border)' : 'none', cursor: 'pointer' }}>
                <span style={{ fontFamily: 'monospace', color: 'var(--primary)', fontWeight: 600, minWidth: 60 }}>{formatTime(c.start)}</span>
                <span>{c.title}</span>
              </div>
            ))}
          </div>
        )}

        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-title" style={{ marginBottom: 12 }}>النصّ الكامل</div>
          <div dir="rtl" style={{ lineHeight: 2.4, fontSize: 15, whiteSpace: 'pre-wrap' }}>
            {alignedTokens.map((tok, i) => {
              if (tok.isWhitespace) return tok.text;
              const isActive = tok.start !== null && currentTime >= tok.start && currentTime < (tok.end ?? tok.start + 0.5);
              return (
                <span key={i} ref={isActive ? activeWordRef : null}
                  onClick={() => tok.start !== null && seekTo(tok.start)}
                  style={{ cursor: tok.start !== null ? 'pointer' : 'default', background: isActive ? 'var(--accent-light)' : 'transparent', borderRadius: 3, padding: '1px 2px', fontWeight: isActive ? 600 : 400 }}>
                  {tok.text}
                </span>
              );
            })}
          </div>
        </div>

        {refs.length > 0 && (
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-title" style={{ marginBottom: 8 }}>📚 الآيات والأحاديث</div>
            {refs.map((r, i) => (
              <div key={i} style={{ padding: 10, background: r.type === 'quran' ? 'var(--accent-light)' : 'var(--info-light)', borderInlineEnd: `4px solid ${r.type === 'quran' ? 'var(--accent)' : 'var(--info)'}`, borderRadius: 6, marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600 }}>{r.type === 'quran' ? '﴾ آية ﴿' : '« حديث »'}</div>
                <div>{r.text}</div>
                {(r.surah || r.ayah || r.source || r.narrator) && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    {[r.surah, r.ayah && `آية ${r.ayah}`, r.source, r.narrator].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <footer style={{ textAlign: 'center', padding: 20, fontSize: 12, color: 'var(--text-muted)' }}>
          <a href="/" style={{ color: 'var(--primary)' }}>منصّة مُفرِّغ</a>
        </footer>
      </div>
    </div>
  );
}
